import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Bookmark, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, euro } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = [
  ["", "Tutti"],
  ["manicure", "Manicure"],
  ["ricostruzione", "Ricostruzione"],
  ["nailart", "Nail Art"],
  ["design", "Design"],
  ["pedicure", "Pedicure"],
];

export default function KimaLooks() {
  const [looks, setLooks] = useState([]);
  const [liked, setLiked] = useState([]);
  const [saved, setSaved] = useState([]);
  const [cat, setCat] = useState("");
  const [sort, setSort] = useState("recent");
  const { user } = useAuth();
  const nav = useNavigate();

  const load = () => {
    api.get(`/looks?sort=${sort}${cat ? `&category_id=${cat}` : ""}`).then((r) => setLooks(r.data)).catch(() => {});
    if (user) api.get("/my/look-likes").then((r) => { setLiked(r.data.liked || []); setSaved(r.data.saved || []); }).catch(() => {});
  };
  useEffect(load, [cat, sort, user]);

  const requireLogin = () => {
    toast.error("Accedi al tuo KIMA ID per continuare");
    nav("/accedi?return=/looks");
  };

  const like = async (id) => {
    if (!user) return requireLogin();
    try {
      const { data } = await api.post(`/looks/${id}/like`);
      setLiked((p) => (data.liked ? [...p, id] : p.filter((x) => x !== id)));
      setLooks((p) => p.map((l) => (l.id === id ? { ...l, likes_count: data.likes_count } : l)));
    } catch { toast.error("Errore"); }
  };

  const save = async (id) => {
    if (!user) return requireLogin();
    try { await api.post("/my/save-look", { look_id: id }); setSaved((p) => [...p, id]); toast.success("Salvato nel tuo KIMA ID"); }
    catch { toast.error("Errore nel salvataggio"); }
  };

  const attribution = (l) => {
    if (l.source !== "client_submission") return "KIMA Atelier";
    return l.attribution === "name" && l.display_name ? l.display_name : "Cliente KIMA";
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="kima-looks-page">
      <span className="kima-overline text-burgundy">Community</span>
      <h1 className="font-serif text-5xl md:text-6xl text-espresso mt-3 mb-4">KIMA Looks</h1>
      <p className="text-espresso/60 max-w-xl mb-10">I look reali della community KIMA. Metti mi piace, salva i tuoi preferiti e prenota lo stesso servizio con un tocco.</p>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map(([k, l]) => (
            <button key={k || "all"} onClick={() => setCat(k)} data-testid={`looks-filter-${k || "all"}`} className={`px-4 py-2 rounded-full text-sm border transition-colors ${cat === k ? "bg-espresso text-ivory border-espresso" : "border-espresso/20 text-espresso/70 hover:border-espresso"}`}>{l}</button>
          ))}
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="looks-sort" className="border border-espresso/20 rounded-sm px-3 py-2 text-sm bg-offwhite">
          <option value="recent">Più recenti</option>
          <option value="popular">Più apprezzati</option>
        </select>
      </div>

      {looks.length === 0 && <p className="text-espresso/50">Nessun look in questa categoria per ora.</p>}
      <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
        {looks.map((l) => (
          <div key={l.id} data-testid={`look-card-${l.id}`} className="break-inside-avoid border border-espresso/10 bg-offwhite rounded-sm overflow-hidden">
            <div className="hover-zoom relative">
              <img src={l.image} alt={l.title} className="w-full object-cover" />
              <button onClick={() => save(l.id)} data-testid={`save-look-${l.id}`} className={`absolute top-3 right-3 p-2 rounded-full transition-colors ${saved.includes(l.id) ? "bg-burgundy text-ivory" : "bg-offwhite/90 hover:bg-burgundy hover:text-ivory"}`}><Bookmark size={16} /></button>
            </div>
            <div className="p-4">
              <h3 className="font-serif text-xl text-espresso">{l.title}</h3>
              <div className="text-xs text-taupe mt-1">di {attribution(l)}</div>
              <div className="flex items-center gap-3 text-xs text-espresso/60 mt-2">
                {l.duration_min ? <span className="flex items-center gap-1"><Clock size={12} /> {l.duration_min}min</span> : null}
                {l.price ? <span>{euro(l.price)}</span> : null}
              </div>
              <div className="flex items-center justify-between mt-3">
                <button onClick={() => like(l.id)} data-testid={`like-look-${l.id}`} className={`flex items-center gap-1.5 text-sm transition-colors ${liked.includes(l.id) ? "text-burgundy" : "text-espresso/60 hover:text-burgundy"}`}>
                  <Heart size={16} fill={liked.includes(l.id) ? "currentColor" : "none"} /> <span data-testid={`like-count-${l.id}`}>{l.likes_count || 0}</span>
                </button>
              </div>
              <button onClick={() => nav(l.service_id ? `/prenota?service=${l.service_id}&look=${l.id}` : `/prenota?look=${l.id}`)} data-testid={`book-look-${l.id}`} className="mt-3 w-full bg-espresso text-ivory py-2 rounded-sm text-sm hover:bg-burgundy transition-colors">Prenota questo look</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
